'use client'

import Text from '../../text'
import Image from 'next/image'
import { MenuItem, ImageAd } from '@/lib/api/navigation/navigation.types'
import Link from 'next/link'
import MobileNavigation from '../mobile-navigation'
import { Fragment, MouseEventHandler, PropsWithChildren, useState } from 'react'
import SubmenuContent from '../shared/submenu-content'
import DesktopContent from '../desktop/content'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import Search from '../../search'
import UserMenu from './user-menu'
import LanguageMenu from './language-menu'
import ChannelSwitcher from './channel-switcher/channel-switcher'
import { isProduction } from '@/lib/utils/environment'
import { SalesChannel } from '@/lib/api/shopware/shopware.types'
import { Language } from '@/localization/localization.types'
import ShoppingCartButton from './shopping-cart-button'
import WishlistButton from './wishlist-button'
import { Heart } from 'lucide-react'
import { Button } from '@/components/ui/button'
import ShoppingBag from '@/components/icons/shopping-bag'
import { Dictionary } from '@/localization/dictionary'

interface ActionProps extends PropsWithChildren {
  name: string
  href?: string
  callback?: MouseEventHandler<HTMLButtonElement>
}

interface NavigationContainerProps {
  menuItems: MenuItem[]
  imageAds: ImageAd[]
  lang: Language
  salesChannel: SalesChannel
  dict?: Dictionary['searchView']
}

const NavigationContainer = ({
  menuItems,
  imageAds,
  lang,
  salesChannel,
  dict,
}: NavigationContainerProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const actions: ActionProps[] = [
    {
      name: 'search',
      children: <Search dict={dict} />,
    },
    {
      name: 'cart',
      children: (
        <ShoppingCartButton
          fallback={
            <Button variant={'plain'} size={'plain'}>
              <ShoppingBag className="size-6 text-black" />
            </Button>
          }
        />
      ),
    },
    {
      name: 'wishlist',
      children: (
        <WishlistButton
          fallback={
            <Button variant={'plain'} size={'plain'}>
              <Heart strokeWidth={1} className="size-6 text-black" />
            </Button>
          }
        />
      ),
    },
  ]

  return (
    <div className={'bg-grey-50'}>
      <div
        className={`relative flex justify-between px-5 transition-all sm:px-0 sm:max-md:flex-wrap md:mx-auto md:max-w-nav md:flex-nowrap md:gap-20`}
      >
        <div className="flex-1 max-md:relative sm:w-full">
          <div className="mx-auto flex w-full max-w-nav flex-1 justify-between transition-all sm:max-md:px-7.5">
            <div className="py-2.5">
              <Link href={'/'}>
                <Image
                  priority
                  width={169}
                  height={60}
                  quality={100}
                  src={'/images/logo.svg'}
                  alt={'/images/logo.png'}
                  className={
                    'h-[calc(27.99/16*1rem)] w-[calc(93/16*1rem)] object-cover transition-all sm:max-md:h-[calc(37.28/16*1rem)] sm:max-md:w-[calc(124/16*1rem)] md:h-[calc(50.8/16*1rem)] md:w-[calc(169/16*1rem)]'
                  }
                />
              </Link>
            </div>

            <div className="hidden items-center sm:flex">
              <div className="items-center justify-center gap-8 sm:flex">
                {menuItems.map((item, index) => {
                  return !item.url ? (
                    <Popover
                      key={`NavigationMenuPopover${index}`}
                      open={isOpen}
                      onOpenChange={setIsOpen}
                    >
                      <PopoverTrigger>
                        <Text
                          as="p"
                          size={14}
                          lineHeight={24}
                          className="uppercase text-secondary-900 hover:text-primary-600"
                        >
                          {item.name}
                        </Text>
                      </PopoverTrigger>

                      <PopoverContent className="hidden w-screen overflow-y-auto rounded-none border-0 bg-transparent p-0 sm:block sm:h-[calc(100vh-57.27px)] md:h-full">
                        <div className="flex h-[calc(100vh-57.27px)] flex-col bg-transparent">
                          <div className={'bg-white'}>
                            <div className="mx-auto flex h-max w-full max-w-screen-2xl bg-white">
                              <SubmenuContent
                                submenuItems={item.subitems}
                                className={'hidden h-lvh sm:max-md:block'}
                                imageAds={imageAds}
                                setIsOpen={setIsOpen}
                              />
                              <DesktopContent
                                submenuItems={item.subitems}
                                className={'max-md:hidden'}
                                imageAds={imageAds}
                                setIsOpen={setIsOpen}
                              />
                            </div>
                          </div>

                          <div
                            className="h-full bg-black/50 max-md:hidden"
                            onClick={() => setIsOpen(false)}
                          ></div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  ) : item.external ? (
                    <a
                      key={`NavigationMenuLink${index}`}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="uppercase text-secondary-900 hover:text-primary-600"
                    >
                      <Text size={14} lineHeight={24}>
                        {item.name}
                      </Text>
                    </a>
                  ) : (
                    <Link
                      key={`NavigationMenuLink${index}`}
                      href={item.url}
                      className="uppercase text-secondary-900 hover:text-primary-600"
                    >
                      <Text size={14} lineHeight={24}>
                        {item.name}
                      </Text>
                    </Link>
                  )
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="flex max-w-nav justify-end sm:mx-auto sm:max-md:w-full sm:max-md:border-t-[1px]">
          <div className="flex max-w-nav sm:items-center sm:max-md:justify-end sm:max-md:px-7.5 sm:max-md:py-1.5">
            <div className="flex items-center justify-center gap-5">
              {actions.map((item, index) => (
                <Fragment key={`NavigationActions${index}`}>
                  {item.children}
                </Fragment>
              ))}

              <UserMenu
                fallback={
                  <Image
                    priority
                    src={'/icons/people.svg'}
                    height={24}
                    width={24}
                    alt={'people'}
                    className={`size-6 cursor-pointer`}
                  />
                }
              />
              {!isProduction() && (
                <ChannelSwitcher salesChannel={salesChannel} />
              )}
              <div className="hidden sm:block">
                <LanguageMenu lang={lang} />
              </div>
              <MobileNavigation menuItems={menuItems} lang={lang} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NavigationContainer
